import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { formatDate } from "../../common/constant/dates";
import { AdminUserRow, getUserAddresses, getUserPayoutAccount } from "../../common/requests/users/endpoints";
import { PayToDetails, useUnlockedAccounts } from "../../components/PayoutKey";
import { CopyValue, DetailDialog, DetailList, DetailSection, EmptyState, ErrorState, LoadingRows, StatusPill, useResource } from "../../ui";
import { roleLabel, roleTone, tariffPercentText, userKind } from "./userData";
import BringBuyersSection from "./BringBuyersSection";

interface UserDialogProps {
  open: boolean;
  /** The user list is still loading (the id can't be resolved yet) */
  loading: boolean;
  user: AdminUserRow | null;
  /** The id from the URL, shown when it isn't in the list */
  requestedId: string | null;
  isSelf: boolean;
  sellerBusy: boolean;
  onClose: () => void;
  onEditSeller: (focus: "tariff" | "whatsapp") => void;
  onSetSeller: (enable: boolean) => void;
  onEditAccess: () => void;
  onRevokeAccess: () => void;
  onDelete: () => void;
  /** Bring Your Buyers was turned on or off (the pilot list reloads) */
  onBringBuyersChanged?: () => void;
}

/** One user: profile, seller settings, addresses, admin access, and the
 * actions that used to be row icons. */
export default function UserDialog(props: UserDialogProps) {
  const { open, loading, user, requestedId, isSelf, sellerBusy, onClose, onEditSeller, onSetSeller, onEditAccess, onRevokeAccess, onDelete, onBringBuyersChanged } =
    props;
  if (!user) {
    return (
      <DetailDialog open={open} onClose={onClose} title={loading ? "Loading user…" : "User not found"} size="sm" loading={loading}>
        <EmptyState title="No user with this ID" body={requestedId ? <span className="mono">{requestedId}</span> : undefined} />
      </DetailDialog>
    );
  }

  const kind = userKind(user);
  const wasSeller = kind.label !== "Buyer";
  const canDelete = !user.admin_user_id && user.is_seller !== true && !!user.user_id && !isSelf;

  const actions = (
    <>
      {canDelete && (
        <Button variant="outlined" color="error" onClick={onDelete}>
          Delete account
        </Button>
      )}
      {user.admin_user_id && !isSelf && (
        <Button variant="outlined" color="error" onClick={onRevokeAccess}>
          Remove admin access
        </Button>
      )}
      {user.user_id &&
        (user.is_seller ? (
          <Button variant="outlined" color="error" onClick={() => onSetSeller(false)} disabled={sellerBusy}>
            {sellerBusy ? "Working…" : "Turn off seller"}
          </Button>
        ) : (
          <Button variant="outlined" onClick={() => onSetSeller(true)} disabled={sellerBusy}>
            {sellerBusy ? "Working…" : wasSeller ? "Turn seller back on" : "Make seller"}
          </Button>
        ))}
      <Button variant="contained" onClick={onEditAccess}>
        {user.admin_user_id ? "Edit admin access" : "Grant admin access"}
      </Button>
    </>
  );

  return (
    <DetailDialog
      open={open}
      onClose={onClose}
      title={user.display_name || user.email || "User"}
      subtitle={user.display_name ? user.email : undefined}
      status={
        <>
          <StatusPill tone={kind.tone}>{kind.label}</StatusPill>
          {user.role && <StatusPill tone={roleTone(user.role)}>{roleLabel(user.role)}</StatusPill>}
        </>
      }
      actions={actions}
    >
      <DetailSection title="Profile">
        <DetailList
          items={[
            { label: "Username", value: user.display_name || "–" },
            { label: "Email", value: user.email ? <CopyValue value={user.email} /> : "–" },
            { label: "User ID", value: user.user_id ? <CopyValue value={user.user_id} mono /> : "–", full: true },
            { label: "Joined", value: formatDate(user.created_at) },
            { label: "Telegram alerts", value: <StatusPill tone={user.telegram_linked ? "success" : "outline"}>{user.telegram_linked ? "Linked" : "Not linked"}</StatusPill> },
          ]}
        />
      </DetailSection>

      {wasSeller && (
        <DetailSection
          title="Seller"
          action={
            <Button size="small" onClick={() => onEditSeller("tariff")} sx={{ minHeight: 36 }}>
              Edit
            </Button>
          }
        >
          {!user.is_seller && (
            <Typography variant="body2" sx={{ color: C.danger }}>
              Seller is turned off: their listings are hidden. Turning seller back on restores them.
            </Typography>
          )}
          <DetailList
            items={[
              { label: "Seller status", value: <StatusPill tone={kind.tone}>{user.is_seller ? "Active" : "Turned off"}</StatusPill> },
              { label: "Fee (tariff rate)", value: tariffPercentText(user.tariff_rate) },
              {
                label: "WhatsApp (order alerts)",
                value: user.whatsapp_number ? (
                  <CopyValue value={user.whatsapp_number} mono />
                ) : (
                  <Box component="span" sx={{ color: C.muted }}>
                    Not set ·{" "}
                    <Box component="button" type="button" onClick={() => onEditSeller("whatsapp")} sx={{ all: "unset", cursor: "pointer", textDecoration: "underline", color: C.ink }}>
                      add
                    </Box>
                  </Box>
                ),
                full: true,
              },
            ]}
          />
        </DetailSection>
      )}

      {wasSeller && user.user_id && (
        <BringBuyersSection
          key={`byb-${user.user_id}`}
          userId={user.user_id}
          name={user.display_name || user.email || "this seller"}
          onChanged={onBringBuyersChanged}
        />
      )}

      {wasSeller && user.user_id && <PayoutSection key={`payout-${user.user_id}`} userId={user.user_id} />}

      {user.user_id && <AddressesSection key={user.user_id} userId={user.user_id} />}

      <DetailSection title="Admin access">
        {user.admin_user_id ? (
          <DetailList
            items={[
              { label: "Role", value: <StatusPill tone={roleTone(user.role)}>{roleLabel(user.role)}</StatusPill> },
              {
                label: "Pages",
                full: true,
                value:
                  user.role === "superadmin" ? (
                    "All pages"
                  ) : user.permissions.length ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.25 }}>
                      {user.permissions.map((p) => (
                        <StatusPill key={p} tone="outline">
                          {p.replaceAll("_", " ")}
                        </StatusPill>
                      ))}
                    </Box>
                  ) : (
                    "None"
                  ),
              },
            ]}
          />
        ) : (
          <Typography variant="body2" sx={{ color: C.muted }}>
            No dashboard access.
          </Typography>
        )}
      </DetailSection>
    </DetailDialog>
  );
}

/** Saved shipping addresses, primary first. Keyed by user so it never shows
 * the previous user's list while loading. */
/** Where Inflos pays this seller: locked until the owner unlocks the tab. */
function PayoutSection({ userId }: { userId: string }) {
  const { idToken } = useAuth();
  const payout = useResource(async () => (await getUserPayoutAccount(userId, idToken!)).data.payout_account, [userId, idToken], {
    enabled: !!idToken,
    errorText: "Couldn't load payout details.",
  });
  const unlocked = useUnlockedAccounts({ [userId]: payout.data }, "user");
  return (
    <DetailSection title="Payout details">
      {payout.status === "error" ? (
        <ErrorState message={payout.error} onRetry={payout.reload} />
      ) : payout.status !== "ready" ? (
        <LoadingRows rows={2} />
      ) : payout.data ? (
        <PayToDetails account={payout.data} plain={unlocked[userId]} />
      ) : (
        <Typography variant="body2" sx={{ color: C.muted }}>
          No payout details saved yet. Sellers add them under Settings › Selling on inflos.co.
        </Typography>
      )}
    </DetailSection>
  );
}

function AddressesSection({ userId }: { userId: string }) {
  const { idToken } = useAuth();
  const addresses = useResource(async () => (await getUserAddresses(userId, idToken!)).data.addresses, [userId, idToken], {
    enabled: !!idToken,
    errorText: "Couldn't load addresses.",
  });
  return (
    <DetailSection title="Addresses">
      {addresses.status === "error" ? (
        <ErrorState message={addresses.error} onRetry={addresses.reload} />
      ) : addresses.data === null ? (
        <LoadingRows rows={2} />
      ) : addresses.data.length === 0 ? (
        <Typography variant="body2" sx={{ color: C.muted }}>
          No saved addresses.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {addresses.data.map((a) => (
            <Box key={a.address_id} sx={{ border: `1px solid ${C.line}`, borderRadius: "8px", p: 1.25, fontSize: 14 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", fontWeight: 600 }}>
                {[a.first_name, a.last_name].filter(Boolean).join(" ") || "–"}
                {a.is_primary && <StatusPill tone="neutral">Primary</StatusPill>}
              </Box>
              <Box sx={{ color: C.ink2, overflowWrap: "anywhere", whiteSpace: "pre-line" }}>{a.address}</Box>
              <Box sx={{ color: C.muted, fontSize: 13 }}>
                {a.postal_code} · {a.country}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </DetailSection>
  );
}
